import { useId, useState } from "react";
import { toast } from "sonner";
import { todayET } from "@/lib/marketHours";
import { occSymbol, pathOf } from "@/components/desk/DeskAudit";
import { Badge, Button, DecisionBadge, Icon, PathChip, StatCell, TextField, dateTimeET, fmt, shortId, timeET, useNow, etAndLocal, etSyd } from "@/sentinel";

/**
 * ConfirmEnterButton semantics (outlined, dashed until pressed, never filled) with a data-testid —
 * the library component does not forward one. Records a review; never sends an order.
 */
export function AckButton({ confirmed = false, confirmedAt, disabled, onConfirm, testId, title, label, sub }) {
  const nid = useId();
  return (
    <button type="button" className="sn-btn sn-btn-ack" aria-pressed={confirmed} disabled={disabled} aria-describedby={nid} title={title}
      onClick={() => { if (!confirmed && onConfirm) onConfirm(); }} data-testid={testId} style={{ alignSelf: "flex-start" }}>
      <span className="sn-ack-main">
        <Icon name={confirmed ? "ClipboardCheck" : "Check"} />
        {label || (confirmed ? "ENTER acknowledged" : "Confirm ENTER")}
        <span style={{ fontWeight: 400, color: "var(--text-secondary)" }}>— acknowledgement only</span>
      </span>
      <span className="sn-ack-sub" id={nid}>
        {sub || (confirmed ? `Logged ${confirmedAt} · no order was sent` : "Records your review in the audit trail. No order is placed.")}
      </span>
    </button>
  );
}

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function fmtExpiry(sessionDate) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(sessionDate || "")) return "(today)";
  const [y, m, d] = sessionDate.split("-").map(Number);
  return `${String(d).padStart(2, "0")} ${MONTHS[m - 1]} ${String(y).slice(2)}`;
}

/**
 * The frozen exit plan (contract item 15). Older picks carry flat fields instead
 * (profit_target / risk_exit / invalidation / max_entry); those are shown the same way.
 */
export function exitPlanOf(pick) {
  const p = pick?.exit_plan;
  if (p) return p;
  if (!pick || (pick.profit_target == null && pick.risk_exit == null && pick.invalidation == null && pick.max_entry == null)) return null;
  return { profit_target: pick.profit_target, stop: pick.risk_exit, invalidation: pick.invalidation, max_entry: pick.max_entry, legacy: true };
}

/**
 * Can the order text still be copied? Disabled after `valid_until`; with no validity recorded,
 * a 0DTE ticket from a past session has expired by definition.
 */
export function orderValidity(plan, sessionDate, now = new Date()) {
  const until = plan?.valid_until ? Date.parse(plan.valid_until) : NaN;
  if (!Number.isNaN(until)) {
    const label = plan.valid_until_et || timeET(until, { seconds: false });
    return now.getTime() > until
      ? { ok: false, label, reason: `Entry window closed at ${etAndLocal(label, { day: sessionDate })}. The frozen plan is no longer valid.` }
      : { ok: true, label };
  }
  if (sessionDate && sessionDate < todayET(now)) return { ok: false, label: null, reason: "These 0DTE contracts expired with their session." };
  return { ok: true, label: null };
}

const numOrText = (v, kind = "price") => (v == null ? "—" : typeof v === "number" ? fmt(v, kind) : String(v));

/** The exit plan as a compact stat grid. */
export function ExitPlan({ plan, testId = "exit-plan" }) {
  if (!plan) return (
    <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid={`${testId}-missing`}>No exit plan was frozen with this pick.</p>
  );
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid={testId}>
      <span className="sn-micro">Exit plan · frozen at the checkpoint{plan.rules_version ? <> · <span className="sn-num">{plan.rules_version}</span></> : null}</span>
      <div className="sn-ticket-grid" style={{ border: 0, padding: 0 }}>
        {plan.hold === "settlement" ? (
          <StatCell size="sm" label="Exit" value="Hold to settlement" data-testid={`${testId}-hold`} />
        ) : (
          <>
            <StatCell size="sm" label="Profit target" value={numOrText(plan.profit_target)} data-testid={`${testId}-target`} />
            <StatCell size="sm" label="Stop" value={numOrText(plan.stop)} data-testid={`${testId}-stop`} />
          </>
        )}
        <StatCell size="sm" label="Max entry" value={numOrText(plan.max_entry)} data-testid={`${testId}-max-entry`} />
        {plan.hold !== "settlement" && <StatCell size="sm" label="Time exit" value={plan.time_exit_et ? etAndLocal(plan.time_exit_et, { day: sessionDate }) : "—"} data-testid={`${testId}-time-exit`} />}
        <StatCell size="sm" label="Valid until" value={plan.valid_until_et ? etAndLocal(plan.valid_until_et, { day: sessionDate }) : plan.valid_until ? etSyd(plan.valid_until, { seconds: false }) : "—"} data-testid={`${testId}-valid-until`} />
      </div>
      {plan.hold === "settlement" && (
        <span className="sn-help" data-testid={`${testId}-hold-note`}>
          In the 2-year backtest, every tested profit target, stop and time exit lowered the average result versus holding to the close.
        </span>
      )}
      {plan.invalidation && (
        <span className="sn-small sn-muted" style={{ overflowWrap: "anywhere" }} data-testid={`${testId}-invalidation`}>
          <span className="sn-faint">Invalidation · </span><span style={{ color: "var(--text-primary)" }}>{plan.invalidation}</span>
        </span>
      )}
    </div>
  );
}

/**
 * The read-only ticket, in TradeTicket's anatomy (sn-ticket): header, legs with OCC symbols,
 * economics, exit plan, the contracts sizer, the copyable order text and the acknowledgement.
 */
export function DeskTradeTicket({ cand, cp, pick, sessionDate, numState, footer }) {
  const [qty, setQty] = useState(1);
  const [showDetail, setShowDetail] = useState(false);
  const tid = useId();
  const now = useNow(5000);
  if (!cand) return null;

  const isCredit = cand.is_credit;
  const optType = /call/i.test(cand.structure_name || "") ? "call" : "put";
  const flow = isCredit ? "credit" : "debit";
  const net = Math.abs(Number(cand.net_price) || 0);
  const perContract = Math.round(net * 100);

  const n = Math.max(1, Math.min(999, Math.floor(Number(qty) || 1)));
  const totalPremium = perContract * n;
  const totalProfit = (Number(cand.max_profit) || 0) * n;
  const totalRisk = (Number(cand.max_loss) || 0) * n;

  const plan = exitPlanOf(pick);
  const validity = orderValidity(plan, sessionDate, now);

  // Broker-ready vertical order string (thinkorswim-style one-liner) for the operator to enter themselves.
  const expiry = fmtExpiry(sessionDate);
  const verticalStrikes = isCredit ? `${cand.short_strike}/${cand.long_strike}` : `${cand.long_strike}/${cand.short_strike}`;
  const orderString =
    `${isCredit ? "SELL" : "BUY"} ${isCredit ? "-" : "+"}${n} VERTICAL SPXW 100 (Weeklys) ` +
    `${expiry} ${verticalStrikes} ${optType.toUpperCase()} @${net.toFixed(2)} LMT ${isCredit ? "CREDIT" : "DEBIT"}`;
  const validityText = plan?.valid_until
    ? `VALID UNTIL ${etAndLocal(validity.label, { day: sessionDate })} ${sessionDate} (${dateTimeET(plan.valid_until, { seconds: false })}) — do not enter after this time`
    : `VALID ${sessionDate} ONLY — no validity window was frozen with this pick`;
  const exitText = plan
    ? [plan.max_entry != null && `max entry ${numOrText(plan.max_entry)}`, plan.profit_target != null && `target ${numOrText(plan.profit_target)}`, plan.stop != null && `stop ${numOrText(plan.stop)}`, plan.time_exit_et && `time exit ${plan.time_exit_et} ET`, plan.hold === "settlement" && "exit: hold to settlement"].filter(Boolean).join(" · ")
    : "";
  const copyText = [orderString, validityText, exitText].filter(Boolean).join("\n");

  const copyOrder = async () => {
    if (!validity.ok) return;
    try {
      await navigator.clipboard.writeText(copyText);
      toast.success("Order copied with its validity window");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const legs = cand.legs || [];
  const shortLeg = legs.find((l) => l.strike === cand.short_strike) || legs[1];
  const longLeg = legs.find((l) => l.strike === cand.long_strike) || legs[0];
  const legRows = [["Sell to open", shortLeg, cand.short_strike], ["Buy to open", longLeg, cand.long_strike]];
  const spot = cand.model_params?.spot;
  const reasonId = `${tid}-copy-reason`;

  return (
    <section className="sn-ticket" aria-labelledby={tid} data-testid="primary-trade-ticket">
      <div className="sn-ticket-h">
        <DecisionBadge decision={cp?.decision_label} size="compact" muted />
        <h3 id={tid} data-testid="trade-ticket-headline">
          {cand.structure_name} · SPX <span className="sn-num">{fmt(cand.short_strike, "strike")} / {fmt(cand.long_strike, "strike")}</span> {optType} {flow} spread · 0DTE
        </h3>
        {cp?.path_state && <PathChip path={pathOf(cp.path_state)} />}
        <span style={{ marginLeft: "auto" }} className="sn-ro"><Icon name="Eye" size={12} />Read-only ticket</span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="sn-ticket-legs" data-testid="trade-ticket-legs">
          <thead><tr><th>Leg</th><th>Contract</th><th className="r">Strike</th><th className="r">Bid</th><th className="r">Ask</th><th className="r">Mid</th></tr></thead>
          <tbody>
            {legRows.map(([side, leg, strike]) => (
              <tr key={side}>
                <td>{side} <span className="sn-num">{n}×</span> {optType}{leg?.modeled ? <span className="sn-small sn-faint"> · modelled</span> : null}</td>
                <td className="sn-num" style={{ fontSize: 12, fontWeight: 500 }}>{occSymbol(sessionDate, leg?.right || (optType === "call" ? "C" : "P"), strike)}</td>
                <td className="r sn-num">{fmt(strike, "strike")}</td>
                <td className="r sn-num">{fmt(leg?.bid)}</td>
                <td className="r sn-num">{fmt(leg?.ask)}</td>
                <td className="r sn-num">{fmt(leg?.mid)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="sn-ticket-grid" data-testid="trade-ticket-economics">
        <StatCell size="md" label={isCredit ? "Net credit" : "Net debit"} value={net} kind="price" state={numState}
          sub={<>width <span className="sn-num">{cand.width}</span> · ≈ <span className="sn-num">{fmt(perContract, "usd")}</span> per 1-lot</>} />
        <StatCell size="md" label="Max profit" value={cand.max_profit} kind="usd" state={numState} sub="per 1 spread" />
        <StatCell size="md" label="Max risk" value={cand.max_loss} kind="usd" state={numState} sub="per 1 spread" />
        <StatCell size="md" label="Breakeven" value={cand.breakeven} kind="price" state={numState}
          sub={spot != null ? <><span className="sn-num">{fmt(Math.abs(spot - cand.breakeven), "pts")}</span> pts from spot</> : null} />
        <StatCell size="md" label="Net EV / max risk" value={cand.ev_pct != null ? cand.ev_pct * 100 : null} kind="pct" signed state={numState}
          sub={<>EV <span className="sn-num">{fmt(cand.ev, "price", { signed: true })}</span> per spread</>} />
        <StatCell size="md" label="P(profit) · model" value={cand.p_profit} kind="prob" state={numState} />
      </div>

      <div className="sn-ticket-f">
        <ExitPlan plan={plan} />

        {(cand.warning_flags || []).length > 0 && (
          <p className="sn-row sn-small" style={{ margin: 0, alignItems: "flex-start", flexWrap: "nowrap", color: "var(--text-secondary)" }} data-testid="primary-warnings">
            <Icon name="Info" size={14} style={{ color: "var(--validation-unconfirmed)", marginTop: 1 }} />
            <span style={{ overflowWrap: "anywhere" }}>
              {(cand.warning_flags || []).includes("CREDIT_TAIL_SOFT") ? "CREDIT_TAIL_SOFT · tail risk is elevated (soft warning; T1 passed)." : (cand.warning_flags || []).join(", ")}
            </span>
          </p>
        )}

        <div className="sn-hr" />
        <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-4)" }}>
          <TextField id="contracts-input" label="Contracts" type="number" min={1} max={999} numeric value={qty}
            onChange={(e) => setQty(e.target.value)} suffix="× spread" data-testid="contracts-input" style={{ width: 72 }} />
          <div className="sn-row" style={{ gap: "var(--space-6)" }} data-testid="contracts-totals">
            <StatCell size="sm" label={isCredit ? "Total credit" : "Total debit"} value={totalPremium} kind="usd" data-testid="sizer-premium" />
            <StatCell size="sm" label="Total max profit" value={totalProfit} kind="usd" data-testid="sizer-profit" />
            <StatCell size="sm" label="Total max risk" value={totalRisk} kind="usd" data-testid="sizer-risk" />
          </div>
        </div>

        <div className="sn-stack" style={{ gap: "var(--space-2)" }}>
          <div className="sn-row" style={{ alignItems: "flex-start" }}>
            <Button size="sm" icon="Copy" onClick={copyOrder} disabled={!validity.ok} aria-describedby={!validity.ok ? reasonId : undefined} data-testid="copy-order-btn">
              Copy order text
            </Button>
            {validity.ok && validity.label && <Badge icon="Clock" data-testid="order-valid-until">Valid until {etAndLocal(validity.label, { day: sessionDate })}</Badge>}
            {!validity.ok && <Badge icon="Hourglass" className="sn-prov" data-testid="order-expired">Expired</Badge>}
          </div>
          {!validity.ok && <span className="sn-small" style={{ color: "var(--text-secondary)" }} id={reasonId} data-testid="copy-order-disabled-reason">{validity.reason}</span>}
          <code className="sn-num sn-small sn-faint" style={{ wordBreak: "break-all", whiteSpace: "pre-wrap", minWidth: 0 }} data-testid="order-string">{copyText}</code>
        </div>

        {footer}

        <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
          <span className="sn-small sn-faint" data-testid="trade-ticket-disclaimer">
            You place it yourself · Candidate <span className="sn-num">{shortId(cand.candidate_id)}</span>{cp?.timestamp_et ? <> · sealed <span className="sn-num">{etSyd(cp.timestamp_et)}</span></> : null}
            {" "}<button type="button" className="sn-link" aria-expanded={showDetail} onClick={() => setShowDetail((v) => !v)} data-testid="ticket-detail-toggle">{showDetail ? "Less" : "Details"}</button>
          </span>
          {showDetail && (
            <span className="sn-small sn-faint" data-testid="primary-diagnostic-score">
              Diagnostic score <span className="sn-num">{cand.diagnostic_score ?? "—"}/100</span> (display only; never overrides a gate) · slippage <span className="sn-num">{fmt(cand.slippage_pts)}</span> · prices are targets and fills may differ; a stop is not a guaranteed maximum loss.
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
